
import { useEffect, useState } from 'react'

import { type ChartItem, fetchChartData, fetchMultipleChartData, hasValidChartData } from '../services/chartDataService'
import type { ChartVariant } from '../utils/variantMapper'

export type UseChartDataResult = {
  data: ChartItem[]
  isLoading: boolean
  error: Error | null
  hasData: boolean
}

export function useChartData(variant: ChartVariant): UseChartDataResult {
  const [data, setData] = useState<ChartItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    async function loadData() {
      setIsLoading(true)
      setError(null)

      try {
        const chartData = await fetchChartData(variant)

        if (isMounted) {
          setData(chartData)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to load chart data'))
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [variant])

  return {
    data,
    isLoading,
    error,
    hasData: hasValidChartData(data),
  }
}

export function useMultipleChartData(variants: ChartVariant[]): Record<string, UseChartDataResult> {
  const [dataMap, setDataMap] = useState<Record<string, ChartItem[]>>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    async function loadData() {
      setIsLoading(true)
      setError(null)

      try {
        const results = await fetchMultipleChartData(variants)

        if (isMounted) {
          setDataMap(results)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to load chart data'))
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    if (variants.length > 0) {
      loadData()
    }

    return () => {
      isMounted = false
    }
  }, [variants.join(',')])

  return variants.reduce(
    (acc, variant) => {
      acc[variant] = {
        data: dataMap[variant] || [],
        isLoading,
        error,
        hasData: hasValidChartData(dataMap[variant] || []),
      }
      return acc
    },
    {} as Record<string, UseChartDataResult>,
  )
}
